import JsPDF from 'jspdf';
import { sendNotification } from './utils';

function initializePDF () {
  const doc = new JsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const initialX = 20;
  const itemXSpacing = 5;
  const columnXSpacing = 10;

  const initialY = 20;
  const sectionYSpacing = 25;
  const itemYSpacing = 10;
  const underlineYSpacing = 3;

  return {
    doc,
    pageHeight,
    pageWidth,
    initialX,
    initialY,
    itemYSpacing,
    itemXSpacing,
    columnXSpacing,
    underlineYSpacing,
    sectionYSpacing
  };
}

// Auxiliary function to check and create new page if needed
function verifyAvailableSpace (doc, currentHeight, contentHeight, config) {
  const { initialY, pageHeight } = config;
  const heightLeft = pageHeight - currentHeight;

  if (heightLeft < contentHeight) {
    doc.addPage();
    return initialY;
  }

  return currentHeight;
}

// Auxiliary function to set title with underline
function setTitle (doc, x, y, text, underlineYSpacing, fontType = 'helvetica', fontStyle = 'bold') {
  doc.setFont(fontType, fontStyle);

  doc.text(x, y, text);
  doc.line(x, y + underlineYSpacing, x + doc.getTextWidth(text), y + underlineYSpacing);
}

// Auxiliary function to set normal text with text wrapping
function setText (doc, x, y, text, availableWidth, itemYSpacing, fontType = 'helvetica', fontStyle = 'normal') {
  doc.setFont(fontType, fontStyle);

  const wrappedText = doc.splitTextToSize(text, availableWidth);
  let currentY = y;

  for (let indexLine = 0; indexLine < (wrappedText || []).length; indexLine += 1) {
    if (indexLine !== 0) {
      currentY += itemYSpacing;
    }
    doc.text(x, currentY, wrappedText[indexLine]);
  }

  return currentY;
}

function inlineTitleText (doc, sessionDataItem, config, y) {
  const { initialX, itemXSpacing, underlineYSpacing, pageWidth, itemYSpacing } = config;
  let dynamicHeight = y;

  setTitle(doc, initialX, dynamicHeight, sessionDataItem.label, underlineYSpacing);

  const labelWidth = doc.getTextWidth(sessionDataItem.label);
  const valueXPosition = initialX + labelWidth + itemXSpacing;
  const availableWidth = pageWidth - valueXPosition - initialX;

  dynamicHeight = setText(doc, valueXPosition, dynamicHeight, sessionDataItem.value, availableWidth, itemYSpacing);

  return dynamicHeight;
}

function renderTable (doc, contentTypeData, config, y) {
  const { initialX, itemYSpacing, underlineYSpacing, pageWidth, columnXSpacing } = config;
  let dynamicHeight = y;

  const availableWidth = pageWidth - (initialX * 2);
  const columnWidth = (availableWidth / contentTypeData.length);

  // Column Headers
  contentTypeData.forEach((contentType, index) => {
    const columnX = index === 0 ? initialX : initialX + (index * columnWidth) + columnXSpacing;
    setTitle(doc, columnX, dynamicHeight, contentType.label, underlineYSpacing);
  });

  dynamicHeight += underlineYSpacing;

  // Get the maximum number of rows across all content types
  const maxRows = Math.max(...contentTypeData.map(content => content.value.length));

  // Data Rows
  for (let i = 0; i < maxRows; i += 1) {
    dynamicHeight += itemYSpacing;

    for (let columnIndex = 0; columnIndex < contentTypeData.length; columnIndex += 1) {
      const contentType = contentTypeData[columnIndex];
      const columnX = columnIndex === 0 ? initialX : initialX + (columnIndex * columnWidth) + columnXSpacing;

      if (i < contentType.value.length && contentType.value[i]) {
        dynamicHeight = setText(doc, columnX, dynamicHeight, contentType.value[i], columnWidth, itemYSpacing);
      }
    }
  }

  return dynamicHeight;
}

function sessionDataSection (doc, sessionData, config) {
  const { initialY, itemYSpacing } = config;
  let dynamicHeight = initialY;

  Object.keys(sessionData).forEach(key => {
    dynamicHeight += itemYSpacing;
    dynamicHeight = inlineTitleText(doc, sessionData[key], config, dynamicHeight);
  });

  return dynamicHeight;
}

function contentTypeDataSection (doc, contentTypeData, config, y) {
  const { itemYSpacing } = config;

  if (!contentTypeData || !contentTypeData.length) {
    return y;
  }

  let dynamicHeight = y;

  // Get the maximum number of rows across all content types
  const maxRows = Math.max(...contentTypeData.map(content => content.value.length));

  // Check if height is enough for all the content type data, if not, add a new page
  dynamicHeight = verifyAvailableSpace(doc, dynamicHeight, (maxRows + 1) * itemYSpacing, config);

  // Render the table
  dynamicHeight = renderTable(doc, contentTypeData, config, dynamicHeight);

  return dynamicHeight;
}

function downloadSessionPDF (sessionData, contentTypeData, filename, text = { success: 'Données de table importées avec succès', error: 'La table n\'a pas de données' }) {
  if (!Object.keys(sessionData).length) {
    sendNotification('error', text.error);
    return;
  }

  const config = initializePDF();
  const { doc, sectionYSpacing } = config;

  let currentHeight = sessionDataSection(doc, sessionData, config);

  currentHeight += sectionYSpacing;

  contentTypeDataSection(doc, contentTypeData, config, currentHeight);

  doc.save(`${filename}.pdf`);
  sendNotification('success', text.success);
}

export default downloadSessionPDF;
